import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import moment from 'moment/moment';
import { EventService, InsuranceProvidersService, JobsService } from '../../../../services';
import { InputHelper } from '../../../../utils/input.helper';
import { SelectItem } from 'primeng';
import { VMEChangeJobDetailsJob } from '../../../../interfaces/screen-specific/job-screen';
import { PriceChangeReasonsService } from '../../../../services/price-change-reasons.service';

@Component({
  selector: 'app-change-job-details-dialog',
  templateUrl: './vme-change-job-details-dialog.component.html',
  styleUrls: ['./vme-change-job-details-dialog.component.scss'],
})
export class VmeChangeJobDetailsDialogComponent implements OnInit {
  @Output() dialogOpenChange = new EventEmitter<boolean>();
  public jobDetailsForm: UntypedFormGroup;
  public jobInfoEarliestPickUpTime = null;
  isBusyChangeJobDetails: boolean;
  jobInfo: VMEChangeJobDetailsJob;
  insuranceProviders: SelectItem[] = [];
  priceChangeReasons: SelectItem[] = [];
  private jobTimelineSteps: any[] = [];

  constructor(
    private fb: FormBuilder,
    private eventService: EventService,
    private insuranceProviderService: InsuranceProvidersService,
    public inputHelper: InputHelper,
    private jobService: JobsService,
    private priceChangeReasonService: PriceChangeReasonsService,
  ) {
    this.initForm();
  }

  private _jobId: number;

  get jobId() {
    return this._jobId;
  }

  @Input('jobId')
  set jobId(value: number) {
    this._jobId = value;
  }

  private _isShowChangeJobDetails: boolean;
  protected hasPriceChanged: boolean = false;

  private get otherReasonValue(): any {
    return this.priceChangeReasons.find((x) => x.label === 'Other')?.value;
  }

  get isOtherReasonSelected(): boolean {
    const other = this.otherReasonValue;
    return other != null && this.f.priceChangeReason?.value === other;
  }

  public get isShowChangeJobDetails() {
    return this._isShowChangeJobDetails;
  }

  @Input('isShowChangeJobDetails')
  public set isShowChangeJobDetails(value: boolean) {
    this._isShowChangeJobDetails = value;

    if (value == true) {
      this.initInsuranceProviders(this.jobId);
      this.initJob(this.jobId);
    }
  }

  get f() {
    return this.jobDetailsForm.controls;
  }

  async ngOnInit() {
    this.initPriceChangeReasons();
  }

  async initPriceChangeReasons() {
    this.priceChangeReasons = await this.priceChangeReasonService.getPriceChangeReasons();
  }

  initInsuranceProviders(jobId: number) {
    this.insuranceProviders = [];
    this.insuranceProviderService.getProvidersByJobId(jobId).then((result) => {
      this.insuranceProviders.push({ label: 'Select provider...', value: null });
      result.forEach((item) =>
        this.insuranceProviders.push({ label: item.insuranceProvider, value: item.id }),
      );
    });
  }

  changeJobDetailsValid() {
    return !(isNaN(this.f.buyItNowPrice.value) || isNaN(this.f.existingCapacityPrice.value));
  }

  cancelChangeJobDetails() {
    // DB CLOSE MODAL
    this.isShowChangeJobDetails = false;
    this.hasPriceChanged = false;
  }

  initForm() {
    this.jobDetailsForm = this.fb.group({
      exisingCapacityEndDate: [],
      heldEndDate: [],
      buyNowOpensDate: [],
      scheduledDate: [],
      buyItNowPrice: [],
      existingCapacityPrice: [],
      supplierPrice: [],
      insuranceCost: [],
      insuranceProviderId: [],
      priceChangeReason: null,
      priceChangeReasonText: null,
    });
  }

  updateForm(job: VMEChangeJobDetailsJob) {
    const timeline = this.fillJobTimeline(job);

    this.jobDetailsForm = this.fb.group({
      exisingCapacityEndDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      heldEndDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      buyNowOpensDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      priceChangeReason: new UntypedFormControl(null, (validator: UntypedFormControl) => {
        if (!validator.value && this.hasPriceChanged) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      priceChangeReasonText: new UntypedFormControl(null, (validator: UntypedFormControl) => {
        if (!validator.value && this.hasPriceChanged && this.isOtherReasonSelected) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),

      scheduledDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        // Ignore time aspect
        let JobEarliest = new Date(this.jobInfoEarliestPickUpTime);
        JobEarliest.setHours(0, 0, 0, 0);
        let ValidDate = new Date(validator.value);
        ValidDate.setHours(0, 0, 0, 0);

        if (ValidDate < JobEarliest) {
          validator.markAsDirty();
          return { valid: false, error: 'This value must be greater than earliest task date' };
        }

        let JobLatest = new Date(timeline.jobInfoLatestDropOffTime);
        JobLatest.setDate(JobLatest.getDate() + 7);
        JobLatest.setHours(0, 0, 0, 0);

        if (ValidDate > JobLatest) {
          validator.markAsDirty();
          return {
            valid: false,
            error: 'This value must be within 7 days of the latest task date',
          };
        }

        return null;
      }),
      buyItNowPrice: new UntypedFormControl(0, null),
      existingCapacityPrice: new UntypedFormControl(0, null),
      supplierPrice: new UntypedFormControl(0, null),
      insuranceCost: new UntypedFormControl(0, null),
      insuranceProviderId: new UntypedFormControl(null),
    });

    this.jobDetailsForm.patchValue({
      buyItNowPrice: job.buyItNowPrice,
      existingCapacityPrice: job.existingCapacityPrice,
      supplierPrice: job.supplierPrice,
      insuranceCost: job.insuranceCost,
      insuranceProviderId: job.insuranceProviderId,
      scheduledDate:
        typeof job.scheduledDate == 'string'
          ? new Date(this.jobInfo.scheduledDate)
          : job.scheduledDate,
      exisingCapacityEndDate:
        typeof job.exisingCapacityEndDate == 'string'
          ? new Date(this.jobInfo.exisingCapacityEndDate)
          : job.exisingCapacityEndDate,
      heldEndDate:
        typeof job.heldEndDate == 'string' ? new Date(this.jobInfo.heldEndDate) : job.heldEndDate,

      buyNowOpensDate:
        typeof job.exisingCapacityEndDate == 'string'
          ? new Date(this.jobInfo.exisingCapacityEndDate)
          : job.exisingCapacityEndDate,
    });

    this.jobDetailsForm.clearValidators();
  }

  fillJobTimeline(jobInfo: VMEChangeJobDetailsJob): JobTimeLine {
    let timeLine: JobTimeLine = {
      jobInfoEarliestPickUpTime: null,
      jobInfoLatestPickUpTime: null,
      jobInfoEarliestDropOffTime: null,
      jobInfoLatestDropOffTime: null,
      jobTimelineSteps: [],
      jobTimelineCurrentStep: 0,
    };

    this.jobTimelineSteps = [];

    if (!jobInfo) {
      return timeLine;
    }

    if (jobInfo.createdOn) {
      this.jobTimelineSteps.push({
        name: 'Job submitted',
        date: new Date(jobInfo.createdOn),
        isCustomer: false,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Job submitted', date: null, isCustomer: false });
    }

    if (jobInfo.orders.length == 1) {
      if (jobInfo.heldEndDate) {
        this.jobTimelineSteps.push({
          name: 'Released from hold',
          date: new Date(jobInfo.heldEndDate),
          isCustomer: false,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Released from hold', date: null, isCustomer: false });
      }
    }

    if (jobInfo.exisingCapacityEndDate) {
      this.jobTimelineSteps.push({
        name: 'Buy it now opens',
        date: new Date(jobInfo.exisingCapacityEndDate),
        isCustomer: false,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Buy it now opens', date: null, isCustomer: false });
    }

    jobInfo.orders.forEach((order) => {
      order.tasks.forEach((task) => {
        if (
          timeLine.jobInfoEarliestPickUpTime == null ||
          timeLine.jobInfoEarliestPickUpTime > new Date(task.pickupEarliestDateTime)
        ) {
          timeLine.jobInfoEarliestPickUpTime = new Date(task.pickupEarliestDateTime);
        }

        if (
          timeLine.jobInfoLatestPickUpTime == null ||
          timeLine.jobInfoLatestPickUpTime < new Date(task.pickupLatestDateTime)
        ) {
          timeLine.jobInfoLatestPickUpTime = new Date(task.pickupLatestDateTime);
        }

        if (
          timeLine.jobInfoEarliestDropOffTime == null ||
          timeLine.jobInfoEarliestDropOffTime > new Date(task.dropoffEarliestDateTime)
        ) {
          timeLine.jobInfoEarliestDropOffTime = new Date(task.pickupEarliestDateTime);
        }

        if (
          timeLine.jobInfoLatestDropOffTime == null ||
          timeLine.jobInfoLatestDropOffTime < new Date(task.dropoffLatestDateTime)
        ) {
          timeLine.jobInfoLatestDropOffTime = new Date(task.dropoffLatestDateTime);
        }
      });
    });

    if (this.jobInfoEarliestPickUpTime) {
      this.jobTimelineSteps.push({
        name: 'Pick-up earliest',
        date: this.jobInfoEarliestPickUpTime,
        isCustomer: true,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Pick-up earliest', date: null, isCustomer: true });
    }

    if (timeLine.jobInfoEarliestDropOffTime > timeLine.jobInfoLatestPickUpTime) {
      if (timeLine.jobInfoLatestPickUpTime) {
        this.jobTimelineSteps.push({
          name: 'Pick-up latest',
          date: timeLine.jobInfoLatestPickUpTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Pick-up latest', date: null, isCustomer: true });
      }

      if (timeLine.jobInfoEarliestDropOffTime) {
        this.jobTimelineSteps.push({
          name: 'Drop-off earliest',
          date: timeLine.jobInfoEarliestDropOffTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Drop-off earliest', date: null, isCustomer: true });
      }
    } else {
      if (timeLine.jobInfoEarliestDropOffTime) {
        this.jobTimelineSteps.push({
          name: 'Drop-off earliest',
          date: timeLine.jobInfoEarliestDropOffTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Drop-off earliest', date: null, isCustomer: true });
      }

      if (timeLine.jobInfoLatestPickUpTime) {
        this.jobTimelineSteps.push({
          name: 'Pick-up latest',
          date: timeLine.jobInfoLatestPickUpTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Pick-up latest', date: null, isCustomer: true });
      }
    }

    if (timeLine.jobInfoLatestDropOffTime) {
      this.jobTimelineSteps.push({
        name: 'Drop-off latest',
        date: timeLine.jobInfoLatestDropOffTime,
        isCustomer: true,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Drop-off latest', date: null, isCustomer: true });
    }

    let today = new Date();
    let i = 0;
    let isExistDate = false;

    for (; i < this.jobTimelineSteps.length; i++) {
      if (
        this.jobTimelineSteps[i].date &&
        this.jobTimelineSteps[i].date.getTime() > today.getTime()
      ) {
        isExistDate = true;
        break;
      }
    }

    if (!isExistDate) {
      i = 0;
      for (; i < this.jobTimelineSteps.length; i++) {
        if (this.jobTimelineSteps[i].date == null) {
          break;
        }
      }
    }

    this.jobTimelineSteps.splice(i, 0, {
      name: 'Current date time',
      date: today,
      isCustomer: true,
    });
    timeLine.jobTimelineCurrentStep = i + 1;

    return timeLine;
  }

  saveChangeJobDetails() {
    this.isBusyChangeJobDetails = true;
    this.jobService
      .changeJobDetails({
        jobId: this.jobId,
        heldEndDate: moment(this.f.heldEndDate.value).format('YYYY-MM-DD HH:mm'),
        exisingCapacityEndDate: moment(this.f.exisingCapacityEndDate.value).format(
          'YYYY-MM-DD HH:mm',
        ),
        scheduledDate: moment(this.f.scheduledDate.value).format('YYYY-MM-DD HH:mm'),
        buyItNowPrice: this.f.buyItNowPrice.value,
        existingCapacityPrice: this.f.existingCapacityPrice.value,
        supplierPrice: this.f.supplierPrice.value,
        insuranceCost: this.f.insuranceCost.value,
        insuranceProviderId: this.f.insuranceProviderId.value,
        priceChangeReasonId: this.f.priceChangeReason.value,
        priceChangeReasonText: this.f.priceChangeReasonText.value,
      })
      .then(() => {
        this.isBusyChangeJobDetails = false;
        this.dialogOpenChange.emit(false);
        this.eventService.JobScreenEvent.emit({
          event: 'job-details-changed',
          data: { jobId: this.jobId },
          sender: 'saveChangeJobDetails',
        });
      })
      .catch(() => {
        /* Emit this error to other components */
        /* DB-JULY
        this.isBusyChangeJobDetails = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Job Details',
          ),
        );
         */
      });
    // Reset price change flags when dialog closes
    this.hasPriceChanged = false;
  }

  currentDate() {
    return new Date();
  }

  closingDialog() {
    this.dialogOpenChange.emit(false);
  }

  private initJob(jobId: number) {
    this.jobService
      .getForComponent<VMEChangeJobDetailsJob>(jobId, 'vme-change-job-details')
      .then((job) => {
        this.jobInfo = job;
        this.updateForm(job);
      });
  }

  protected setHasPriceChanged() {
    if (this.f.buyItNowPrice.value != this.jobInfo.buyItNowPrice) {
      this.hasPriceChanged = true;
    } else {
      this.hasPriceChanged = false;
    }
  }
}

export interface JobTimeLine {
  jobInfoEarliestPickUpTime: any;
  jobInfoLatestPickUpTime: any;
  jobInfoEarliestDropOffTime: any;
  jobInfoLatestDropOffTime: any;
  jobTimelineSteps: any[];
  jobTimelineCurrentStep: number;
}
